/**
 * Marker SVG API client.
 * Provides URL builders for ArUco and QR marker SVG endpoints.
 * Also provides functions for marker availability checks.
 */

import type { MarkerType } from '@shared/types';

const API_BASE = '/api/v1';

/**
 * Get the URL for an ArUco marker SVG.
 *
 * @param id - ArUco marker ID (0-49)
 * @param size - SVG size in pixels (64-1024, default 256)
 * @param label - Optional label text below the marker
 * @returns URL string for the SVG endpoint
 */
export function getArucoSvgUrl(id: number, size?: number, label?: string): string {
  const params = new URLSearchParams();
  if (size !== undefined) params.set('size', String(size));
  if (label !== undefined) params.set('label', label);
  const qs = params.toString();
  return `${API_BASE}/markers/aruco/${id}/svg${qs ? `?${qs}` : ''}`;
}

/**
 * Get the URL for a QR code marker SVG.
 *
 * @param markerId - The marker ID string
 * @param size - SVG size in pixels (64-1024, default 256)
 * @param label - Optional label text below the QR code
 * @returns URL string for the SVG endpoint
 */
export function getQrSvgUrl(markerId: string, size?: number, label?: string): string {
  const params = new URLSearchParams();
  if (size !== undefined) params.set('size', String(size));
  if (label !== undefined) params.set('label', label);
  const qs = params.toString();
  return `${API_BASE}/markers/qr/${encodeURIComponent(markerId)}/svg${qs ? `?${qs}` : ''}`;
}

/**
 * Get all available (unused) ArUco marker IDs for the current household.
 *
 * @returns Promise resolving to array of available IDs (0-49)
 */
export async function getAvailableArucoIds(): Promise<number[]> {
  const response = await fetch(`${API_BASE}/markers/available-aruco-ids`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch available ArUco IDs: ${response.statusText}`);
  }

  const data = await response.json();
  return data.availableIds;
}

/**
 * Check if a marker (type + ID) is available (not in use).
 *
 * @param markerType - The marker type ('aruco' or 'qr')
 * @param markerId - The marker ID string
 * @returns Promise resolving to true if available, false if in use
 */
export async function checkMarkerAvailable(
  markerType: MarkerType,
  markerId: string,
): Promise<boolean> {
  const params = new URLSearchParams({
    markerType,
    markerId,
  });

  const response = await fetch(`${API_BASE}/markers/check-available?${params.toString()}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to check marker availability: ${response.statusText}`);
  }

  const data = await response.json();
  return data.available;
}

/**
 * Get the URL for a label PDF containing a single marker.
 *
 * @param markerType - The marker type ('aruco' or 'qr')
 * @param markerId - The marker ID
 * @param size - Label size ('small', 'medium', 'large')
 * @param format - Paper format ('letter' or 'a4')
 * @returns URL string for the PDF endpoint
 */
export function getLabelPdfUrl(
  markerType: MarkerType,
  markerId: string,
  size: 'small' | 'medium' | 'large' = 'medium',
  format: 'letter' | 'a4' = 'letter',
): string {
  const markers = `${markerType}:${markerId}`;
  const params = new URLSearchParams({
    markers,
    size,
    format,
  });
  return `${API_BASE}/labels/pdf?${params.toString()}`;
}
